import DS from 'ember-data';
import EmObject, { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import { dropTask } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import FeaturesService from 'ember-feature-flags/services/features';

import CampaignModel from 'cio/models/campaign';
import ActionModel from 'cio/models/action';
import NewsletterModel, { Channel } from 'cio/models/newsletter';
import TemplateModel from 'cio/models/template';
import TransactionalMessageModel, { TransactionalMessageChannelType } from 'cio/models/transactional-message';
import EnvironmentService from 'cio/services/environment';
import ApiErrorsService from 'cio/services/api-errors';
import { Editor } from 'cio/services/editor';
import { SerializedLegacyAttributeFilter } from 'cio/types/conditions/legacy-filter';
import { getTestGroupTemplates } from 'cio/utils/newsletter-testgroups';

export default interface ComposerEditableEntity {
  message?: TransactionalMessageModel;
  campaign?: CampaignModel;
  action?: ActionModel;
  newsletter?: NewsletterModel;
  template?: TemplateModel;
  model: CampaignModel | NewsletterModel | TransactionalMessageModel;

  isNewsletter: boolean;
  isCampaign: boolean;
  isTransactionalMessage: boolean;

  composerRoute: string;
  composerRouteDefaultParams: string[];

  isEmail: boolean;
  isInApp: boolean;

  supportsLanguages: boolean;
  hasTranslations: boolean;
  languageCount: number;
  language: string;
  translatedLanguages: string[];
  mustSaveBeforeAddingLanguages: boolean;

  analyticsData: ComposerEditableEntityAnalyticsData;

  addLanguageTranslations: (languages: string[]) => unknown;
  deleteLanguageTranslation: (translation: TemplateModel | ActionModel) => unknown;
}

export type ComposerEditableEntityAnalyticsData = {
  isNewsletter: boolean;
  isCampaign: boolean;
  isTransactionalMessage: boolean;

  model: CampaignModel | NewsletterModel | TransactionalMessageModel;
  action?: ActionModel;
  template?: TemplateModel;
  editor?: Editor;

  languageAttribute: string;
  languageCount: number;
  language: string;
};

export interface ComposerEditableEntityArgs {
  message?: TransactionalMessageModel;
  campaign?: CampaignModel;
  action?: ActionModel;
  newsletter?: NewsletterModel;
  template?: TemplateModel;
}

export function createComposerEditableEntity(owner: object, args: ComposerEditableEntityArgs): ComposerEditableEntity {
  let { message, campaign, action, newsletter, template } = args;

  if (campaign != null && action != null && template != null) {
    return new ComposerCampaignActionTemplate(owner, { campaign, action, template });
  }

  if (campaign != null && action != null) {
    return new ComposerCampaignAction(owner, { campaign, action });
  }

  if (newsletter != null && template != null) {
    return new ComposerNewsletterTemplate(owner, { newsletter, template });
  }

  if (message != null && template != null) {
    return new ComposerTransactionMessageTemplate(owner, { message, template });
  }

  throw new Error('Unable to create ComposerEditableEntity');
}

abstract class ComposerEditableEntityBase extends EmObject {
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service features!: FeaturesService;

  model!: CampaignModel | NewsletterModel | TransactionalMessageModel;
  template?: TemplateModel;

  isNewsletter = false;
  isCampaign = false;
  isTransactionalMessage = false;
  abstract get languageCount(): number;
  abstract get language(): string;

  get analyticsData(): ComposerEditableEntityAnalyticsData {
    return {
      isNewsletter: false,
      isCampaign: false,
      isTransactionalMessage: false,
      model: this.model,
      editor: this.template?.editor,
      languageAttribute: this.environment.current.languageAttribute,
      languageCount: this.languageCount,
      language: this.language,
    };
  }
}

export class ComposerCampaignActionTemplate extends ComposerEditableEntityBase implements ComposerEditableEntity {
  campaign!: CampaignModel;
  action!: ActionModel;
  template!: TemplateModel;

  isCampaign = true;

  constructor(owner: object, args: ComposerEditableEntityArgs) {
    super(owner);
    this.campaign = args.campaign as CampaignModel;
    this.action = args.action as ActionModel;
    this.template = args.template as TemplateModel;
    this.model = this.campaign;
  }

  get analyticsData(): ComposerEditableEntityAnalyticsData {
    return {
      ...super.analyticsData,
      isCampaign: true,
      action: this.action,
      template: this.template,
    };
  }

  composerRoute = 'composer.action';

  get composerRouteDefaultParams() {
    return [this.defaultLanguageAction.id];
  }

  get isEmail(): boolean {
    return this.action.isEmail;
  }

  get isInApp(): boolean {
    return this.action.isInApp;
  }

  get supportsLanguages() {
    // Multi-language is not supported for campaign split tests.
    return this.action.isMessagingType && !this.action.isVariation;
  }

  @computed('campaign.parentConsolidatedBranchMap.{}')
  get parentBranch(): ActionModel | null {
    return this.campaign.getConsolidatedActionParentBranch(this.action.id);
  }

  @computed('parentBranch')
  get isMultiLangBranchChild(): boolean {
    return this.parentBranch?.isMultiLangBranch ?? false;
  }

  @computed('campaign.consolidatedActionsGroups.{}')
  get language(): string {
    return this.getMessagingActionLanguage(this.action);
  }

  get mustSaveBeforeAddingLanguages(): boolean {
    if (this.template.get('hasDirtyAttributes')) {
      return true;
    }

    if (isEmpty(this.language)) {
      return isEmpty(this.template.editor) && this.isEmail;
    }

    return false;
  }

  @action
  getMessagingActionLanguage(action: ActionModel): string {
    return this.campaign.getConsolidatedBranchChildActionLanguage(action.id) ?? '';
  }

  @computed('languageCount')
  get hasTranslations() {
    return this.languageCount > 0;
  }

  @computed('campaign.consolidatedActionsGroups.{}')
  get languageCount() {
    return this.campaign.getLanguageCountByMessagingAction(this.action.id);
  }

  @computed('campaign.consolidatedActionsGroups.{}')
  get multiLangBranchChildren(): ActionModel[] {
    return this.campaign.getConsolidatedBranchChildActions(this.parentBranch?.id);
  }

  @computed('parentBranch.multiConditions.[]')
  get translatedLanguages(): string[] {
    return (
      this.parentBranch?.multiConditions
        .map((condition) => {
          return (condition[0] as SerializedLegacyAttributeFilter).value?.toLowerCase() ?? '';
        })
        .filter((language) => isPresent(language)) ?? []
    );
  }

  get defaultLanguageAction(): ActionModel {
    return this.campaign.getConsolidatedActionsGroupDefaultActionByBranchActionId(this.parentBranch?.id) ?? this.action;
  }

  @action
  async addLanguageTranslations(languages: string[]) {
    if (!this.action.isMessagingType) {
      return;
    }

    await this.addLanguageVariationActions.perform(languages);
    this.prefetchSiblingTemplates();
  }

  // Pre-loads translations templates to prevent composer UI reloading on language tab change.
  prefetchSiblingTemplates() {
    for (let action of this.multiLangBranchChildren) {
      let templateId = action.belongsTo('template').id();
      if (this.store.peekRecord('template', templateId) == null) {
        this.store.findRecord('template', templateId);
      }
    }
  }

  private prepareParentBranchActionToAddLanguageVariations(languages: string[]): ActionModel {
    let parentBranchAction: ActionModel;

    if (this.isMultiLangBranchChild) {
      parentBranchAction = this.parentBranch as ActionModel;
    } else {
      parentBranchAction = this.store.createRecord('action', {
        type: 'multi_lang_branch_action',
        id: -1,
        name: '',
        campaign: this.campaign,
      });
    }

    parentBranchAction.addLanguageConditionsToMultiLanguageBranchAction(languages);

    return parentBranchAction;
  }

  private createNewSerializedLanguageVariationActions(languages: string[]): object[] {
    let serializedActions = [];

    // New translations will duplicate the default language message action and template.
    let serializedDefaultLangAction = this.defaultLanguageAction.serialize() as any;

    // New actions have negative IDs until they are saved on backend.
    let parentBranchActionId = this.parentBranch?.id ?? '-1';
    let newActionId = parentBranchActionId === '-1' ? -2 : -1;

    for (let language of languages) {
      let newSerializedLangAction = {
        ...serializedDefaultLangAction,
        _copy: true,
        id: `${newActionId}`,
        // Special case. Backend will set the new copied template's 'language' attribute
        // to the 'template_language' value so we can do things within a single request.
        template_language: language,
      };
      serializedActions.push(newSerializedLangAction);
      newActionId--;
    }

    return serializedActions;
  }

  addLanguageVariationActions = dropTask(async (languages: string[]) => {
    let parentBranchAction = this.prepareParentBranchActionToAddLanguageVariations(languages);

    let serializedActions = this.createNewSerializedLanguageVariationActions(languages);
    serializedActions.push(parentBranchAction.serialize({ includeId: true }));
    if (parentBranchAction.id === '-1') {
      parentBranchAction.unloadRecord();
    }

    let edges = this.campaign.getNewEdgesAfterAddingChildActionsToConsolidatedBranch(
      this.parentBranch?.id,
      this.action.id,
      languages.length,
    );

    try {
      await this.campaign.addActions({
        actions: serializedActions,
        edges,
        copyFromEnv: this.environment.current.id,
        defaultLanguageActionId: this.action.id,
      });
    } catch (err) {
      this.apiErrors.report(err);
      this.campaign.rollbackAttributes();
      this.parentBranch?.rollbackAttributes();
      this.campaign.reload();
    } finally {
      this.campaign.parseTree();
    }
  });

  @action
  async deleteLanguageTranslation(action: ActionModel) {
    await this.deleteLanguageVariationAction.perform(action);
  }

  deleteLanguageVariationAction = dropTask(async (action: ActionModel) => {
    let actionLanguage = this.getMessagingActionLanguage(action).toLowerCase();
    if (isEmpty(actionLanguage)) {
      return;
    }

    let edges = this.campaign
      .expandConsolidatedEdges(this.campaign.edges)
      .filter((edge) => edge.from !== action.id && edge.to !== action.id);

    let multiConditions = (this.parentBranch?.multiConditions ?? []).filter(
      (condition) => (condition[0] as SerializedLegacyAttributeFilter).value?.toLowerCase() !== actionLanguage,
    );
    this.parentBranch?.set('multiConditions', multiConditions);

    try {
      await this.campaign.deleteActions({
        actionIds: [action.id],
        edges,
        touchedActionIds: [],
        skipExpandConsolidatedEdges: true,
      });
    } catch (err) {
      this.campaign.rollbackAttributes();
      this.parentBranch?.rollbackAttributes();
      this.campaign.reload();
      this.apiErrors.report(err);
    } finally {
      this.campaign.unloadActions([action.id]);
      this.campaign.parseTree();
    }
  });
}

export class ComposerCampaignAction extends ComposerEditableEntityBase implements ComposerEditableEntity {
  campaign!: CampaignModel;
  action!: ActionModel;

  isCampaign = true;

  constructor(owner: object, args: ComposerEditableEntityArgs) {
    super(owner);
    this.campaign = args.campaign as CampaignModel;
    this.action = args.action as ActionModel;
    this.model = this.campaign;
  }

  get analyticsData(): ComposerEditableEntityAnalyticsData {
    return {
      ...super.analyticsData,
      isCampaign: true,
      action: this.action,
    };
  }

  composerRoute = 'composer.action';

  get composerRouteDefaultParams() {
    return [this.action.id];
  }

  supportsLanguages = false;
  isEmail = false;
  isInApp = false;
  language = '';
  hasTranslations = false;
  languageCount = 0;
  translatedLanguages = [];
  mustSaveBeforeAddingLanguages = false;
  addLanguageTranslations() {}
  deleteLanguageTranslation() {}
}

export class ComposerNewsletterTemplate extends ComposerEditableEntityBase implements ComposerEditableEntity {
  newsletter: NewsletterModel;
  template: TemplateModel;

  isNewsletter = true;

  constructor(owner: object, args: ComposerEditableEntityArgs) {
    super(owner);
    this.newsletter = args.newsletter as NewsletterModel;
    this.template = args.template as TemplateModel;
    this.model = this.newsletter;
  }

  get analyticsData(): ComposerEditableEntityAnalyticsData {
    return {
      ...super.analyticsData,
      isNewsletter: true,
      template: this.template,
    };
  }

  composerRoute = 'composer.newsletter.template';

  get composerRouteDefaultParams() {
    return [this.newsletter.id, this.defaultTemplate.id];
  }

  supportsLanguages = true;

  @computed('template.language')
  get language(): string {
    return this.template.language ?? '';
  }

  @computed('newsletter.channel')
  get isEmail(): boolean {
    return this.newsletter.channel === Channel.Email;
  }

  get isInApp(): boolean {
    return this.newsletter.channel === Channel.InApp;
  }

  @computed('languageCount')
  get hasTranslations(): boolean {
    return this.languageCount > 0;
  }

  @computed('testGroupTemplates.[]')
  get languageCount(): number {
    return Math.max(this.testGroupTemplates.length - 1, 0);
  }

  @computed('newsletter.activeTemplates.[]')
  get testGroupTemplates(): TemplateModel[] {
    let { activeTemplates } = this.newsletter;
    return getTestGroupTemplates(this.template.testGroupId, activeTemplates);
  }

  @computed('testGroupTemplates.[]')
  get defaultTemplate(): TemplateModel {
    return this.testGroupTemplates.find((template) => isEmpty(template.language)) as TemplateModel;
  }

  @computed('testGroupTemplates.[]')
  get translatedLanguages(): string[] {
    return this.testGroupTemplates.reduce((acc: string[], template) => {
      if (isPresent(template.language)) {
        acc = [...acc, template.language.toLowerCase()];
      }
      return acc;
    }, []);
  }

  get mustSaveBeforeAddingLanguages(): boolean {
    if (!this.isEmail) {
      return false;
    }

    return isEmpty(this.language) && (isEmpty(this.template.editor) || this.template.get('hasDirtyAttributes'));
  }

  @action
  async addLanguageTranslations(languages: string[]) {
    await this.newsletter.addTranslations({
      languages,
      referenceTemplate: this.defaultTemplate,
    });
  }

  @action
  async deleteLanguageTranslation(template: TemplateModel) {
    if (!isEmpty(template.language)) {
      await template.destroyRecord();
    }
  }
}

export class ComposerTransactionMessageTemplate extends ComposerEditableEntityBase implements ComposerEditableEntity {
  message: TransactionalMessageModel;
  template: TemplateModel;

  isTransactionalMessage = true;

  constructor(owner: object, args: ComposerEditableEntityArgs) {
    super(owner);
    this.message = args.message as TransactionalMessageModel;
    this.template = args.template as TemplateModel;
    this.model = this.message;
  }

  get analyticsData(): ComposerEditableEntityAnalyticsData {
    return {
      ...super.analyticsData,
      isTransactionalMessage: true,
      template: this.template,
    };
  }

  composerRoute = 'composer.transactional.template';

  get composerRouteDefaultParams() {
    return [this.message.id, this.message.belongsTo('template').id()];
  }

  supportsLanguages = true;

  get language(): string {
    return this.template.language ?? '';
  }

  get isEmail(): boolean {
    return this.message.type === TransactionalMessageChannelType.Email;
  }

  get isInApp(): boolean {
    return false;
  }

  get languageCount(): number {
    return Math.max(this.message.hasMany('templates').ids().length - 1, 0);
  }

  get hasTranslations(): boolean {
    return this.languageCount > 0;
  }

  get translatedLanguages(): string[] {
    return (
      this.message
        .hasMany('templates')
        .value()
        ?.reduce((acc: string[], template) => {
          if (isPresent(template.language)) {
            acc = [...acc, template.language.toLowerCase()];
          }
          return acc;
        }, []) ?? []
    );
  }

  get mustSaveBeforeAddingLanguages(): boolean {
    return isEmpty(this.language) && (isEmpty(this.template.editor) || this.template.get('hasDirtyAttributes'));
  }

  @action
  async addLanguageTranslations(languages: string[]) {
    await this.message.addTranslations({ languages });
  }

  @action
  async deleteLanguageTranslation(template: TemplateModel) {
    if (!isEmpty(template.language)) {
      await template.destroyRecord();
    }
  }
}
